import { useCallback, useMemo } from 'react';
import type {
  EnvironmentKey,
  ConfigEntityDetail,
  DomainEntityConfiguration,
} from '@web-config-app/core';
import {
  ConfigEntityAuthAction,
  getConfigEntitySchemaWithDomainEntityAttributesAndRelationships,
  hasHttpStatus,
} from '@web-config-app/core';
import { buildConfigEntityAuthHeaders } from '@web-config-app/api';
import { useConfigEntityGetByIdQuery } from '../use-config-entity-get-by-id-query/use-config-entity-get-by-id-query';
import { useConfigApp } from '../use-config-app/use-config-app';
import { useLoadEntityRelationshipsSchemas } from '../use-load-entity-relationships-schemas/use-load-entity-relationships-schemas';
import { useEnvironmentFetch } from '../use-environment-fetch/use-environment-fetch';
import type { FindConfigEntityResult } from '../../types/operation.types';
import {
  transformIdsForImport,
  type ImportIdTransformerResult,
} from './transform-ids-for-import';
import { getEntityEndpoint } from '../../utilities/get-entity-endpoint/get-entity-endpoint.util';
import { getEndpointParamsWithValues } from '../../utilities/get-endpoint-params-with-values/get-endpoint-params-with-values';
import { extractRelationshipConfigEntityRefs } from '../../utilities/get-all-referenced-entities/extract-relationship-config-entity-ids';

export interface UseImportEntityIdTransformerProps {
  targetEnvironment: EnvironmentKey;
  tenantId: string;
}

/**
 * Hook that transforms entity IDs for import.
 *
 * Checks if entities exist in the target environment and generates new IDs
 * for those that don't. Handles both the main entity and relationship entities.
 *
 * Handles relationship configEntityIds by loading the domain entity schema based on the entity's domainEntityType
 * and combines it with the config entity schema to properly resolve x-entity-reference
 * annotations.
 *
 * If the main entity exists in the target environment, it will return transformed: false and the data will be returned as is.
 *
 * @returns Async function: (entityData) => { transformed, data }
 *
 * @example
 * ```typescript
 * const transformIdsForImport = useImportEntityIdTransformer({ targetEnvironment: 'staging', tenantId: 'my-tenant' });
 * const { transformed, data } = await transformIdsForImport(entityData);
 * ```
 */
export const useImportEntityIdTransformer = ({
  targetEnvironment,
  tenantId,
}: UseImportEntityIdTransformerProps) => {
  const { configEntity, entityConfigs } = useConfigApp();
  const fetchEntityById = useConfigEntityGetByIdQuery({
    sourceEnvironment: targetEnvironment,
  });
  const targetEnvironmentFetch = useEnvironmentFetch(targetEnvironment);
  const loadEntityRelationshipsSchemas = useLoadEntityRelationshipsSchemas();

  /**
   * Cache config entity existence checks so we don't check the same entity twice during an import.
   * - 404: confirmed missing → return not_found (cached)
   * - Success: return success (cached)
   * - Other errors (permissions/network/transient): throw (not cached, can retry)
   */
  const createGetEntityById = useCallback(
    (configEntityTypeById: Map<string, string>) => {
      const cache = new Map<string, Promise<FindConfigEntityResult>>();

      return (entityId: string): Promise<FindConfigEntityResult> => {
        const configEntityType = configEntityTypeById.get(entityId);
        const cacheKey = `${configEntityType ?? ''}:${entityId}`;
        const cached = cache.get(cacheKey);
        if (cached) return cached;

        const promise = (async (): Promise<FindConfigEntityResult> => {
          try {
            const response = await fetchEntityById(entityId, configEntityType);
            return { status: 'success', data: response.data };
          } catch (error) {
            if (hasHttpStatus(error, 404)) {
              if (error instanceof Error) {
                return { status: 'not_found', error };
              }
              return {
                status: 'not_found',
                error: new Error(JSON.stringify(error)),
              };
            }
            throw error;
          }
        })();

        // On failure, remove from cache so it can be retried
        const cachedPromise = promise.catch((error) => {
          cache.delete(cacheKey);
          throw error;
        });
        cache.set(cacheKey, cachedPromise);
        return cachedPromise;
      };
    },
    [fetchEntityById],
  );

  /**
   * For linked references (x-entity-reference.domainEntityType), we need to avoid rewriting IDs
   * that already exist in the target tenant (otherwise we'd break links).
   *
   * We do a list-by-domainEntityId existence check against the config-entities list endpoint (`/v1/config-entities`) using:
   * - `filter[domainEntityType]=...`
   * - `filter[domainEntityID]=...`
   * This enables:
   * - Keeping original IDs when the target already has that referenced entity
   * - Rewriting to the deterministic transformed ID when the target *doesn't* have the original
   *   (which also supports the "dependencies imported first" workflow)
   */
  const linkedEntityExists = useMemo(() => {
    const entityConfigByType = new Map(
      Object.values(entityConfigs).map(
        (entityConfig: DomainEntityConfiguration) => [
          entityConfig.type,
          entityConfig,
        ],
      ),
    );

    /**
     * Cache existence checks so we don't check the same entity twice during an import.
     * Failures are not cached so they can be retried.
     */
    const cache = new Map<string, Promise<boolean>>();

    return async (domainEntityType: string, domainEntityId: string) => {
      const cacheKey = `${domainEntityType}:${domainEntityId}`;
      const cached = cache.get(cacheKey);
      if (cached) return cached;

      const promise = (async () => {
        const entityConfig = entityConfigByType.get(domainEntityType);
        // If we can't resolve the referenced type, we can't reliably check.
        // This is a configuration error and should fail the import flow.
        if (!entityConfig) {
          throw new Error(
            `linkedEntityExists: unknown domain entity type "${domainEntityType}"`,
          );
        }

        const listEndpoint = configEntity.endpoints.list;
        /**
         * If we cannot determine existence (no config-entities list endpoint),
         * treat this as a configuration error and fail the import flow. This should never happen.
         */
        if (!listEndpoint) {
          throw new Error(
            'linkedEntityExists: configEntity has no list endpoint; cannot check domainEntityId existence',
          );
        }

        const { endpointFetch } = getEntityEndpoint(
          listEndpoint,
          targetEnvironmentFetch,
        );
        const params = getEndpointParamsWithValues([
          { name: 'filter[domainEntityID]', value: domainEntityId },
          { name: 'filter[domainEntityType]', value: domainEntityType },
          { name: 'page[size]', value: '1' },
          { name: 'page[offset]', value: '0' },
        ]);

        try {
          const response = await endpointFetch({
            params,
            headers: buildConfigEntityAuthHeaders(
              domainEntityType,
              ConfigEntityAuthAction.Get,
            ),
          });
          const items = Array.isArray(response?.data) ? response.data : [];
          return items.length > 0;
        } catch (error) {
          /**
           * Error handling strategy:
           * - 404: confirmed missing → return false (doesn't exist)
           * - anything else (permissions/network/transient/etc): throw the API error
           */
          if (hasHttpStatus(error, 404)) return false;
          throw error;
        }
      })();

      // On failure, remove from cache so it can be retried
      const cachedPromise = promise.catch((error) => {
        cache.delete(cacheKey);
        throw error;
      });
      cache.set(cacheKey, cachedPromise);
      return cachedPromise;
    };
  }, [configEntity.endpoints.list, entityConfigs, targetEnvironmentFetch]);

  return useCallback(
    async (data: ConfigEntityDetail): Promise<ImportIdTransformerResult> => {
      const domainEntityType =
        data?.attributes?.entityMetadata?.domainEntityType;

      if (!domainEntityType) {
        throw new Error(
          'Domain entity type is required for import entity id transformation',
        );
      }

      // Load the domain entity schema for this entity type
      const [domainEntitySchema] = await loadEntityRelationshipsSchemas([
        domainEntityType,
      ]);

      if (domainEntitySchema.status === 'error' || !domainEntitySchema.schema) {
        throw new Error(
          `Unable to load schema for domain entity type: ${domainEntityType}`,
        );
      }

      const combinedSchema =
        getConfigEntitySchemaWithDomainEntityAttributesAndRelationships(
          configEntity.schema,
          domainEntitySchema.schema,
        );

      // Build a map of config entity ID → domain entity type for auth headers.
      // The root entity's type comes from its metadata; relationship entity types
      // are extracted from the schema (the relationship data's `type` field is
      // always the generic "config-entity", not the domain-specific type).
      const configEntityTypeById = new Map<string, string>([
        [data.id, domainEntityType],
      ]);

      const relationshipRefs = extractRelationshipConfigEntityRefs(
        data,
        combinedSchema,
      );
      relationshipRefs.forEach((ref) => {
        if (ref.domainEntityType) {
          configEntityTypeById.set(ref.id, ref.domainEntityType);
        }
      });

      return transformIdsForImport(
        data,
        createGetEntityById(configEntityTypeById),
        combinedSchema,
        tenantId,
        linkedEntityExists,
      );
    },
    [
      createGetEntityById,
      configEntity.schema,
      loadEntityRelationshipsSchemas,
      tenantId,
      linkedEntityExists,
    ],
  );
};
